import { useState } from 'react'
import { Alert, Button, Form, Input, Typography } from 'antd'
import { QRCodeSVG } from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { AuthShell } from './AuthShell'
import type { TenantChoiceState } from './TenantChoicePage'

/** Birinchi marta (yoki reset'dan keyin) — QR skanerlanadi */
export interface TotpSetupState {
  stage: 'setup'
  setupToken: string
  qrUri: string
  secret: string
  from: string
}

/** Har safargi kirish — faqat kod so'raladi */
export interface TotpVerifyState {
  stage: 'verify'
  totpToken: string
  from: string
}

type TotpState = TotpSetupState | TotpVerifyState

const CODE_LENGTH = 6

/**
 * Bir martalik kod bosqichi — kirish oqimining IKKINCHI qadami.
 *
 * ⚠️ HOLAT FAQAT NAVIGATSIYA ORQALI KELADI (`location.state`), URL'da
 * emas. Oqim tokeni (`setupToken` / `totpToken`) — qisqa umrli sir;
 * uni manzil satriga qo'yish brauzer tarixiga, jurnal va «havolani
 * ulashish» yo'liga chiqarardi. Holatsiz ochilsa `/login` ga qaytadi.
 *
 * ⚠️ NOTO'G'RI KODDA SESSIYA YOPILMAYDI. Server oqim tokenini faqat kod
 * TO'G'RI bo'lganda sarflaydi (`LoginFlowService#verifyTotp`), ya'ni
 * qayta urinish kutilgan holat. Shuning uchun bu ekran xatoni bannerda
 * ko'rsatadi va o'sha joyda qoladi — `/login` ga uloqtirmaydi. Admin
 * panelida aynan shu nuqson XSB-005 sifatida o'lchangan.
 */
export function TotpPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { completeTotpSetup, completeTotpVerify } = useAuth()

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const state = location.state as TotpState | null
  if (!state || (state.stage !== 'setup' && state.stage !== 'verify')) {
    return <Navigate to="/login" replace />
  }

  const handleFinish = (values: { code?: string }) => {
    const code = (values.code ?? '').trim()
    if (code.length !== CODE_LENGTH) return

    setSubmitting(true)
    setError(null)

    const request =
      state.stage === 'setup'
        ? completeTotpSetup(state.setupToken, code)
        : completeTotpVerify(state.totpToken, code)

    void request
      .then((outcome) => {
        switch (outcome.kind) {
          case 'authenticated':
            navigate(state.from, { replace: true })
            return
          case 'chooseTenant': {
            const next: TenantChoiceState = {
              choiceToken: outcome.choiceToken,
              candidates: outcome.candidates,
              from: state.from,
            }
            navigate('/login/tenant', { state: next })
            return
          }
          case 'noMembership':
            setError(t('auth.totp.noMembership'))
        }
      })
      .catch((err: unknown) => setError(translateError(err, t)))
      .finally(() => setSubmitting(false))
  }

  const isSetup = state.stage === 'setup'

  return (
    <AuthShell
      title={isSetup ? t('auth.totp.setupTitle') : t('auth.totp.verifyTitle')}
      subtitle={isSetup ? t('auth.totp.setupSubtitle') : t('auth.totp.verifySubtitle')}
    >
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      {isSetup ? (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          {/* ⚠️ QR OQ FONDA. Qorong'i mavzuda shaffof qoldirilsa skaner
              kontrastni topa olmaydi — kassirning telefoni aynan
              shunday holatda ishlaydi. */}
          <div style={{ display: 'inline-block', background: '#ffffff', padding: 12, borderRadius: 12 }}>
            <QRCodeSVG value={state.qrUri} size={168} />
          </div>
          <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 4 }}>
            {t('auth.totp.scanHint')}
          </Typography.Paragraph>
          {/* Skaner ishlamasa sir qo'lda kiritiladi — telefonda kamera
              ruxsati yopiq bo'lishi odatiy hol. */}
          <Typography.Text code copyable>
            {state.secret}
          </Typography.Text>
        </div>
      ) : null}

      <Form onFinish={handleFinish} disabled={submitting} layout="vertical" requiredMark={false}>
        <Form.Item
          name="code"
          label={t('auth.totp.enterCode')}
          rules={[{ required: true, len: CODE_LENGTH, message: t('auth.totp.enterCode') }]}
        >
          {/* `inputMode="numeric"` — telefonda RAQAMLI klaviatura ochiladi;
              usiz kassir har safar harflardan raqamlarga o'tardi. */}
          <Input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            style={{ letterSpacing: 6, textAlign: 'center', fontSize: 20 }}
          />
        </Form.Item>

        <Button type="primary" htmlType="submit" block loading={submitting}>
          {t('auth.signIn')}
        </Button>
      </Form>
    </AuthShell>
  )
}
